import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, User, FileText, Loader2, Printer, RotateCcw, Send,
  CreditCard, Banknote, Building2, CheckCircle, Clock, AlertTriangle,
  Plus, History, Trash2, Download, FileCode
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: 'Bearer ' + getToken() }, withCredentials: true });

const typeLabels = { normal: 'Normală', advance: 'Avans', final: 'Finală', storno: 'Storno' };
const typeColors = { normal: 'bg-blue-50 text-blue-700', advance: 'bg-amber-50 text-amber-700', final: 'bg-emerald-50 text-emerald-700', storno: 'bg-red-50 text-red-700' };
const emissionLabels = { draft: 'Draft', emitted: 'Emisă', fully_stornoed: 'Stornată total', partially_stornoed: 'Stornată parțial' };
const paymentLabels = { unpaid: 'Neplătită', partially_paid: 'Parțial plătită', paid: 'Plătită', cancelled: 'Anulată' };
const paymentColors = { unpaid: 'text-amber-600', partially_paid: 'text-blue-600', paid: 'text-emerald-600', cancelled: 'text-slate-500' };
const spvLabels = { not_ready: 'Nepregătit', ready: 'Pregătit SPV', submitted: 'Trimis SPV', accepted: 'Acceptat', rejected: 'Respins' };
const spvColors = { not_ready: 'bg-slate-100 text-slate-500', ready: 'bg-blue-50 text-blue-600', submitted: 'bg-indigo-50 text-indigo-600', accepted: 'bg-green-50 text-green-600', rejected: 'bg-red-50 text-red-600' };
const methodLabels = { cash: 'Cash', card: 'Card', bank_transfer: 'Transfer bancar' };
const methodIcons = { cash: Banknote, card: CreditCard, bank_transfer: Building2 };

export default function AdminInvoiceDetail() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);

  // Payment dialog
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'cash', payment_date: '', payment_reference: '', notes: '' });
  const [addingPayment, setAddingPayment] = useState(false);

  // Storno dialog
  const [stornoOpen, setStornoOpen] = useState(false);
  const [stornoType, setStornoType] = useState('total');
  const [stornoReason, setStornoReason] = useState('');
  const [selectedStornoLines, setSelectedStornoLines] = useState([]);
  const [creatingStorno, setCreatingStorno] = useState(false);

  // SPV
  const [preparingSpv, setPreparingSpv] = useState(false);
  const [submittingSpv, setSubmittingSpv] = useState(false);

  useEffect(() => { fetchInvoice(); fetchAudit(); }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(API + '/admin/billing/invoices/' + invoiceId, authHeaders());
      setInvoice(res.data);
    } catch { toast.error('Eroare la încărcarea facturii'); }
    finally { setLoading(false); }
  };

  const fetchAudit = async () => {
    try {
      const res = await axios.get(API + '/admin/billing/audit/' + invoiceId, authHeaders());
      setAudit(res.data);
    } catch { /* silent */ }
  };

  // Emit draft
  const emitDraft = async () => {
    try {
      await axios.post(API + '/admin/billing/invoices/' + invoiceId + '/emit', {}, authHeaders());
      toast.success('Factură emisă cu succes');
      fetchInvoice(); fetchAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  // Payment
  const addPayment = async () => {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) { toast.error('Introduceți suma'); return; }
    setAddingPayment(true);
    try {
      await axios.post(API + '/admin/billing/payments', {
        invoice_id: invoiceId,
        amount: Number(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_date: paymentForm.payment_date || new Date().toISOString().slice(0, 10),
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes
      }, authHeaders());
      toast.success('Plată înregistrată');
      setPaymentOpen(false);
      setPaymentForm({ amount: '', payment_method: 'cash', payment_date: '', payment_reference: '', notes: '' });
      fetchInvoice(); fetchAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setAddingPayment(false); }
  };

  // Storno
  const createStorno = async () => {
    if (stornoType === 'partial' && selectedStornoLines.length === 0) { toast.error('Selectați liniile de stornat'); return; }
    setCreatingStorno(true);
    try {
      const data = { storno_type: stornoType, reason: stornoReason };
      if (stornoType === 'partial') {
        data.selected_lines = selectedStornoLines.map(lineId => ({ line_id: lineId, amount_factor: 1.0 }));
      }
      await axios.post(API + '/admin/billing/invoices/' + invoiceId + '/storno', data, authHeaders());
      toast.success('Factură storno creată');
      setStornoOpen(false);
      fetchInvoice(); fetchAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setCreatingStorno(false); }
  };

  // SPV
  const prepareSpv = async () => {
    setPreparingSpv(true);
    try {
      await axios.post(API + '/admin/billing/invoices/' + invoiceId + '/spv/prepare', {}, authHeaders());
      toast.success('Factură pregătită pentru SPV');
      fetchInvoice(); fetchAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setPreparingSpv(false); }
  };

  const submitSpv = async () => {
    if (!confirm('Confirmați trimiterea facturii în SPV?')) return;
    setSubmittingSpv(true);
    try {
      await axios.post(API + '/admin/billing/invoices/' + invoiceId + '/spv/submit', {}, authHeaders());
      toast.success('Factură trimisă în SPV');
      fetchInvoice(); fetchAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setSubmittingSpv(false); }
  };

  const toggleStornoLine = (lineId) => {
    setSelectedStornoLines(prev => prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  if (!invoice) return <div className="text-center py-12 text-slate-500">Factură negăsită</div>;

  const canAddPayment = ['emitted', 'partially_stornoed'].includes(invoice.emission_status) && invoice.payment_status !== 'paid' && invoice.payment_status !== 'cancelled' && !invoice.is_storno;
  const canStorno = invoice.emission_status === 'emitted' && !invoice.is_storno && !invoice.storno_invoice_id;
  const canEmit = invoice.emission_status === 'draft';
  const canPrepareSpv = invoice.emission_status === 'emitted' && invoice.spv_status === 'not_ready';
  const canSubmitSpv = invoice.spv_status === 'ready';
  const canDelete = !['submitted', 'accepted'].includes(invoice.spv_status);

  const deleteInvoice = async () => {
    if (!confirm(`Sigur doriți să ștergeți factura ${invoice.invoice_number || 'Draft'}?`)) return;
    try {
      await axios.delete(API + '/admin/billing/invoices/' + invoiceId, authHeaders());
      toast.success('Factură ștearsă');
      window.location.href = '/admin/invoices';
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la ștergere'); }
  };

  const downloadPdf = () => {
    const token = getToken();
    window.open(API + '/admin/billing/invoices/' + invoiceId + '/pdf?token=' + token, '_blank');
  };

  const downloadXml = () => {
    const token = getToken();
    window.open(API + '/admin/billing/invoices/' + invoiceId + '/xml?token=' + token, '_blank');
  };

  const printReceipt = (paymentId) => {
    const token = getToken();
    window.open(API + '/admin/billing/payments/' + paymentId + '/receipt?token=' + token, '_blank');
  };

  return (
    <div className="space-y-5" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/admin/invoices"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Înapoi</Button></Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold" data-testid="invoice-number">{invoice.invoice_number || 'Draft'}</h1>
            <Badge className={typeColors[invoice.invoice_type]}>{typeLabels[invoice.invoice_type]}</Badge>
            {emissionLabels[invoice.emission_status] && invoice.emission_status !== 'emitted' && (
              <Badge className={`text-xs ${invoice.emission_status === 'draft' ? 'bg-slate-100 text-slate-600' : invoice.emission_status.includes('storno') ? 'bg-orange-50 text-orange-600' : ''}`}>{emissionLabels[invoice.emission_status]}</Badge>
            )}
            <Badge className={spvColors[invoice.spv_status]}>{spvLabels[invoice.spv_status]}</Badge>
          </div>
          <p className="text-sm text-slate-500">
            Emisă: {invoice.issue_date || '-'} {invoice.due_date && `• Scadentă: ${invoice.due_date}`}
            {invoice.order_id && <> • <Link to={`/admin/orders/${invoice.order_id}`} className="text-blue-600 hover:underline">Comandă</Link></>}
          </p>
        </div>
        <div className="flex gap-2">
          {canEmit && <Button onClick={emitDraft} className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="emit-draft-btn"><CheckCircle className="h-4 w-4" />Emite</Button>}
          <Button onClick={downloadPdf} variant="outline" className="gap-1" data-testid="download-pdf-btn"><Download className="h-4 w-4" />PDF</Button>
          <Button onClick={downloadXml} variant="outline" className="gap-1" data-testid="download-xml-btn"><FileCode className="h-4 w-4" />XML SPV</Button>
          {canAddPayment && <Button onClick={() => { setPaymentForm(p => ({ ...p, amount: String(invoice.remaining_amount || '') })); setPaymentOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="add-payment-btn"><Plus className="h-4 w-4" />Plată</Button>}
          {canStorno && <Button variant="outline" className="text-red-600 border-red-300 gap-1" onClick={() => setStornoOpen(true)} data-testid="storno-btn"><RotateCcw className="h-4 w-4" />Storno</Button>}
          {canDelete && <Button variant="outline" className="text-red-600 border-red-300 gap-1" onClick={deleteInvoice} data-testid="delete-invoice-btn"><Trash2 className="h-4 w-4" />Șterge</Button>}
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Detalii</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Plăți ({invoice.payments?.length || 0})</TabsTrigger>
          <TabsTrigger value="spv" data-testid="tab-spv">SPV</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Istoric</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Client */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><User className="h-4 w-4 text-blue-600" />Client</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Nume</span><p className="font-medium">{invoice.client?.name || 'N/A'}</p></div>
                  <div><span className="text-slate-500">CNP/CUI</span><p>{invoice.client?.cnp_cui || 'N/A'}</p></div>
                  <div><span className="text-slate-500">Email</span><p>{invoice.client?.email || 'N/A'}</p></div>
                  <div><span className="text-slate-500">Telefon</span><p>{invoice.client?.phone || 'N/A'}</p></div>
                  {invoice.client?.address && <div className="col-span-2"><span className="text-slate-500">Adresă</span><p>{invoice.client.address}</p></div>}
                </CardContent>
              </Card>

              {/* Lines */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4 text-blue-600" />Linii factură</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm" data-testid="invoice-lines-table">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Descriere</th>
                        <th className="text-center px-3 py-2 font-medium text-slate-500">Cant.</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-500">Preț unitar RON</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-500">TVA RON</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-500">Total RON</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lines?.map((line, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 ${line.is_advance_deduction ? 'bg-amber-50' : ''}`}>
                          <td className="px-4 py-2.5">
                            <div>
                              {line.description}
                              {line.is_advance_deduction && <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">Deducere avans</Badge>}
                            </div>
                            {line.unit_price_eur > 0 && !line.is_advance_deduction && (
                              <span className="text-xs text-slate-400">{line.unit_price_eur?.toFixed(2)} EUR</span>
                            )}
                          </td>
                          <td className="text-center px-3 py-2.5">{line.quantity}</td>
                          <td className="text-right px-3 py-2.5">{line.unit_price_ron?.toFixed(2)}</td>
                          <td className="text-right px-3 py-2.5 text-slate-500">{line.vat_amount_ron?.toFixed(2)}</td>
                          <td className="text-right px-4 py-2.5 font-medium">{line.total_ron?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 bg-slate-50">
                        <td colSpan={4} className="px-4 py-2 text-right font-semibold text-slate-600">TOTAL RON:</td>
                        <td className="px-4 py-2 text-right font-bold text-lg text-blue-700">{invoice.total_ron?.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </CardContent>
              </Card>

              {invoice.notes && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Notițe</CardTitle></CardHeader>
                  <CardContent><p className="text-sm text-slate-600">{invoice.notes}</p></CardContent>
                </Card>
              )}
            </div>

            {/* Summary sidebar */}
            <div className="space-y-5">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Sumar financiar</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal RON</span><span>{invoice.subtotal_ron?.toFixed(2)}</span></div>
                  {invoice.vat_total_ron > 0 && <div className="flex justify-between"><span className="text-slate-500">TVA RON</span><span>{invoice.vat_total_ron?.toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t pt-2"><span className="font-semibold">Total RON</span><span className="text-xl font-bold text-blue-700">{invoice.total_ron?.toFixed(2)}</span></div>
                  <div className="border-t pt-2 space-y-1">
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Curs BNR: {invoice.exchange_rate_bnr}</span>
                      <span>+{invoice.exchange_rate_margin}%</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400"><span>Curs aplicat</span><span>{invoice.exchange_rate_final} RON/EUR</span></div>
                    <div className="flex justify-between text-xs text-slate-400"><span>Echivalent EUR (informativ)</span><span>{invoice.total_eur?.toFixed(2)} EUR</span></div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Status plată</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Status</span>
                    <span className={`font-semibold ${paymentColors[invoice.payment_status]}`}>{paymentLabels[invoice.payment_status]}</span>
                  </div>
                  <div className="flex justify-between"><span className="text-slate-500">Plătit</span><span className="font-medium text-emerald-600">{invoice.total_paid?.toFixed(2)} RON</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Rest de plată</span><span className="font-medium text-amber-600">{invoice.remaining_amount?.toFixed(2)} RON</span></div>
                  {/* Progress bar */}
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, invoice.total_ron > 0 ? (invoice.total_paid / invoice.total_ron) * 100 : 0)}%` }} />
                  </div>
                </CardContent>
              </Card>

              {invoice.is_storno && invoice.original_invoice_id && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-red-700 font-medium">Factură storno</p>
                    <p className="text-xs text-red-600 mt-1">Tip: {invoice.storno_type === 'total' ? 'Total' : 'Parțial'}</p>
                    {invoice.storno_reason && <p className="text-xs text-red-600 mt-1">Motiv: {invoice.storno_reason}</p>}
                    <Link to={`/admin/invoices/${invoice.original_invoice_id}`} className="text-xs text-blue-600 hover:underline mt-1 block">Vezi factura originală</Link>
                  </CardContent>
                </Card>
              )}
              {invoice.storno_invoice_id && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-orange-700 font-medium">Factură stornată</p>
                    <Link to={`/admin/invoices/${invoice.storno_invoice_id}`} className="text-xs text-blue-600 hover:underline mt-1 block">Vezi factura storno</Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Plăți înregistrate</CardTitle>
              {canAddPayment && (
                <Button size="sm" onClick={() => { setPaymentForm(p => ({ ...p, amount: String(invoice.remaining_amount || '') })); setPaymentOpen(true); }} className="bg-blue-600 text-white gap-1" data-testid="add-payment-tab-btn">
                  <Plus className="h-3 w-3" />Adaugă plată
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {(!invoice.payments || invoice.payments.length === 0) ? (
                <p className="text-sm text-slate-500 text-center py-8">Nicio plată înregistrată</p>
              ) : (
                <div className="space-y-3">
                  {invoice.payments.map((p, idx) => {
                    const Icon = methodIcons[p.payment_method] || CreditCard;
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg" data-testid={`payment-${idx}`}>
                        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center border"><Icon className="h-5 w-5 text-slate-600" /></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{p.amount?.toFixed(2)} {p.currency}</span>
                            <Badge variant="outline" className="text-xs">{methodLabels[p.payment_method] || p.payment_method}</Badge>
                            {p.receipt_number && <Badge variant="outline" className="text-xs text-green-600 border-green-300">Chitanță: {p.receipt_number}</Badge>}
                          </div>
                          <p className="text-xs text-slate-500">{p.payment_date} {p.payment_reference && `• Ref: ${p.payment_reference}`}</p>
                          {p.notes && <p className="text-xs text-slate-400 mt-1">{p.notes}</p>}
                        </div>
                        {p.receipt_number && (
                          <Button variant="outline" size="sm" className="gap-1" onClick={() => printReceipt(p.payment_id)} data-testid={`print-receipt-${idx}`}>
                            <Printer className="h-3 w-3" />Chitanță
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SPV Tab */}
        <TabsContent value="spv">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" />SPV / e-Factura</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={`${spvColors[invoice.spv_status]} px-3 py-1`}>{spvLabels[invoice.spv_status]}</Badge>
                {invoice.spv_submission_id && <span className="text-sm text-slate-500">ID: {invoice.spv_submission_id}</span>}
              </div>

              {invoice.spv_submitted_at && (
                <div className="text-sm text-slate-600">
                  <p>Trimis la: {new Date(invoice.spv_submitted_at).toLocaleString('ro-RO')}</p>
                  {invoice.spv_response_message && <p className="mt-1 text-slate-500">{invoice.spv_response_message}</p>}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {canPrepareSpv && (
                  <Button onClick={prepareSpv} disabled={preparingSpv} variant="outline" className="gap-1" data-testid="prepare-spv-btn">
                    {preparingSpv ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    Pregătește SPV
                  </Button>
                )}
                {canSubmitSpv && (
                  <Button onClick={submitSpv} disabled={submittingSpv} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1" data-testid="submit-spv-btn">
                    {submittingSpv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Trimite în SPV
                  </Button>
                )}
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700">SPV/e-Factura funcționează în mod simulare. Trimiterea reală necesită certificate digitale ANAF și configurare completă.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" />Istoric acțiuni</CardTitle></CardHeader>
            <CardContent>
              {audit.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Nicio acțiune înregistrată</p>
              ) : (
                <div className="space-y-2">
                  {audit.map((a, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2.5 border-b last:border-0" data-testid={`audit-${idx}`}>
                      <div className="h-2 w-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{a.action.replace(/_/g, ' ')}</p>
                        {a.notes && <p className="text-xs text-slate-500">{a.notes}</p>}
                        {a.new_values && <p className="text-xs text-slate-400 mt-0.5">{JSON.stringify(a.new_values)}</p>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{new Date(a.created_at).toLocaleString('ro-RO')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Înregistrare plată</DialogTitle>
            <DialogDescription>Adaugă o plată pentru factura {invoice.invoice_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sumă (RON) *</Label>
              <Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} data-testid="payment-amount" />
              <p className="text-xs text-slate-500 mt-1">Rest de plată: {invoice.remaining_amount?.toFixed(2)} RON</p>
            </div>
            <div>
              <Label>Metoda de plată *</Label>
              <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm(p => ({ ...p, payment_method: v }))}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash (Numerar)</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Transfer bancar (OP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data plății</Label>
              <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm(p => ({ ...p, payment_date: e.target.value }))} data-testid="payment-date" />
            </div>
            <div>
              <Label>Referință / Nr. tranzacție</Label>
              <Input value={paymentForm.payment_reference} onChange={e => setPaymentForm(p => ({ ...p, payment_reference: e.target.value }))} placeholder="ex: OP-12345" data-testid="payment-reference" />
            </div>
            <div>
              <Label>Notițe</Label>
              <Textarea value={paymentForm.notes} onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="payment-notes" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPaymentOpen(false)}>Anulează</Button>
              <Button onClick={addPayment} disabled={addingPayment} className="bg-blue-600 text-white" data-testid="submit-payment-btn">
                {addingPayment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Înregistrează
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Storno Dialog */}
      <Dialog open={stornoOpen} onOpenChange={setStornoOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Stornare factură</DialogTitle>
            <DialogDescription>Creați o factură storno pentru {invoice.invoice_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tip storno</Label>
              <Select value={stornoType} onValueChange={v => setStornoType(v)}>
                <SelectTrigger data-testid="storno-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">Storno Total</SelectItem>
                  <SelectItem value="partial">Storno Parțial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {stornoType === 'partial' && invoice.lines && (
              <div>
                <Label className="mb-2 block">Selectați liniile de stornat</Label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {invoice.lines.filter(l => !l.is_advance_deduction).map((line, idx) => (
                    <label key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedStornoLines.includes(line.line_id) ? 'bg-red-50 border-red-300' : 'bg-white hover:bg-slate-50'}`}>
                      <input type="checkbox" checked={selectedStornoLines.includes(line.line_id)} onChange={() => toggleStornoLine(line.line_id)} className="rounded border-slate-300" />
                      <div className="flex-1">
                        <p className="text-sm">{line.description}</p>
                        <p className="text-xs text-slate-500">{line.total_ron?.toFixed(2)} RON</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Motiv storno</Label>
              <Textarea value={stornoReason} onChange={e => setStornoReason(e.target.value)} rows={2} placeholder="Motivul stornării..." data-testid="storno-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStornoOpen(false)}>Anulează</Button>
              <Button onClick={createStorno} disabled={creatingStorno} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-storno-btn">
                {creatingStorno && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Creează Storno
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
